import { css } from 'styled-components';
import { DeprecatedLinkButton as LinkButton, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { Icon } from '@/app/common/icons/Icon';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useChangeTransportOpenModal } from '@/app/components/hotel-detail/evo/transport/EvoChangeTransportCard/ChangeTransportModalProvider';
import { useTrackChangeTransportLinkClicked } from '@/app/components/hotel-detail/evo/hooks/useTrackChangeTransportLinkClicked.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { FLIGHTS_DATES_FORMAT, getFlightDates } from '@/app/components/hotel-detail/common/dates';
import { getCheckedBaggageText } from '@/app/components/hotel-detail/common/luggageTranslations';
import { SummaryHeader, SummaryRow, SummaryRows, SummarySection } from './summarySection.styles';
import { TransportLegRow } from './TransportLegRow';
import { SummaryRowsSkeleton } from './SummaryRowsSkeleton';

/**
 * Flight summary for the mobile trip overview: header (origin city + change
 * link), one stacked row per leg — "LHR – PMI · Tue, 13 Aug (06:45 - 09:55)" —
 * and the included-luggage rows. The desktop sidebar uses TransportSummarySection,
 * which shares the same stacked TransportLegRow but wraps the legs in a button that
 * opens the flight-details modal.
 */
export function MobileTransportSummary({ showModifyLink = true }: { showModifyLink?: boolean }) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { locale } = localization;
  const { offersData, isLoadingRooms } = useOffersContext();
  const { adults, children, isTrain } = useUrlSearchParams();
  const { openModal: openChangeTransportModal } = useChangeTransportOpenModal();
  const trackChangeTransportLinkClicked = useTrackChangeTransportLinkClicked();
  const { selectedBrandedFare } = useSelectedMealPlan();

  const transport = offersData?.transport?.transports?.[0];
  const outbound = transport?.outbound;
  const inbound = transport?.inbound;

  // Curated offers include checked bags per booking (numberOfBaggagePiecesIncluded), not per passenger.
  const numberOfBaggagePiecesIncluded = transport?.numberOfBaggagePiecesIncluded ?? 0;
  const showGroupLuggage = numberOfBaggagePiecesIncluded > 0;
  // MPH-932: the summary promises what the whole trip carries, so a piece counts only when both
  // bounds include it — the outbound alone used to decide, and an inbound without a cabin bag
  // still read "2 x cabin bags".
  const checkedBagIncluded =
    !!selectedBrandedFare?.holdBaggage || (outbound?.includeBaggage && inbound?.includeBaggage) || showGroupLuggage;
  const carryOnBagIncluded =
    !!selectedBrandedFare?.cabinBaggage || (outbound?.handBaggageIncluded && inbound?.handBaggageIncluded);
  const numberOfLuggage = adults + children;

  const originTransportCity = outbound?.segments?.[0]?.departureHub.cityName;

  const flightDays =
    outbound && inbound
      ? getFlightDates(locale, outbound.departureDate, inbound.departureDate, FLIGHTS_DATES_FORMAT)
      : null;
  // MPH-1144: same loading state as the desktop TransportSummarySection — the legs and the luggage
  // rows show together once the transport has loaded, until then three skeleton lines and no
  // change link.
  const isTransportLoaded = !isLoadingRooms && outbound && inbound && flightDays;

  return (
    <SummarySection>
      <SummaryHeader>
        {/* MPH-842: Figma sizes this header as Title4 (18/28). The tag stays an h5 so the
            heading outline is unchanged — the block is a subsection of the trip overview,
            whose own title is the h4. */}
        <Text variant="Title4" forceTag="h5">
          {originTransportCity
            ? t(
                isTrain
                  ? 'vacation.select.cc.trip_overview.train_from'
                  : 'vacation.select.cc.trip_overview.flight_from',
                { origin: originTransportCity }
              )
            : t(
                isTrain
                  ? 'vacation.select.cc.trip_overview.return_train'
                  : 'vacation.select.cc.trip_overview.return_flight'
              )}
        </Text>
        {showModifyLink && isTransportLoaded && (
          <LinkButton
            variant="accent"
            appearance="underlined"
            onClick={() => {
              trackChangeTransportLinkClicked();
              openChangeTransportModal();
            }}
            css={css`
              ${({ theme }) => theme.kallisto.mixins.font('Body')};
              white-space: nowrap;
            `}
          >
            {t('vacation.select.cc.trip_overview.change_flight')}
          </LinkButton>
        )}
      </SummaryHeader>
      <SummaryRows>
        {isTransportLoaded ? (
          <>
            <TransportLegRow leg={outbound} day={flightDays.checkin} />
            <TransportLegRow leg={inbound} day={flightDays.checkout} />

            <SummaryRow>
              <Icon category="Luggage" name="icon-backpack" size={20} color="iconPrimary" alt="small bag" />
              <Text variant="Body">
                {numberOfLuggage > 1
                  ? t('vacation.select.cc.luggage.hand.plural', { num: numberOfLuggage.toString() })
                  : t('vacation.select.cc.luggage.hand.single')}
              </Text>
            </SummaryRow>

            {carryOnBagIncluded && (
              <SummaryRow>
                <Icon category="Luggage" name="icon-carry-on" size={20} color="iconPrimary" alt="carry on bag" />
                <Text variant="Body">
                  {numberOfLuggage > 1
                    ? t('vacation.select.cc.luggage.cabin.plural', { num: numberOfLuggage.toString() })
                    : t('vacation.select.cc.luggage.cabin.single')}
                </Text>
              </SummaryRow>
            )}

            {checkedBagIncluded && (
              <SummaryRow>
                <Icon category="Luggage" name="icon-suitcase" size={20} color="iconPrimary" alt="checked bag" />
                <Text variant="Body">
                  {getCheckedBaggageText({ showGroupLuggage, numberOfBaggagePiecesIncluded, numberOfLuggage, t })}
                </Text>
              </SummaryRow>
            )}
          </>
        ) : (
          <SummaryRowsSkeleton />
        )}
      </SummaryRows>
    </SummarySection>
  );
}
